import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import css from './views.module.css'
import { Card, Disclosure, Empty, MicroLabel, Notice, QueryError, ViewHeader } from '../ui'
import { useFleetStatus } from '../data/queries'
import { clearLine, statusPanelFrom, type StatusJob } from '../data/map/fleetStatus'
import { age, until } from '../lib/time'

/**
 * Status: is anything broken or waiting on me?
 *
 * One read of `fleet_status` answers it. Everything above the fold is what the
 * `fleet-status` agent found on its last pass -- the jobs that are not fine,
 * the inbox's standing, and how long ago it looked. The jobs that are fine are
 * a count behind a closed section, because a list of twenty working timers is
 * the page this one replaces.
 *
 * Nothing here is a control and nothing here is urgent (product rule 1). A
 * failed job is a muted clay word in the same type as a healthy one, there is
 * no badge and no total styled as an alarm, and a job that broke on Sunday sits
 * here until someone has time -- which is the point of a pull-based surface.
 * Each row links out to where the job actually lives, because this panel is
 * where you find out something needs reading, not where you read it.
 */
export function Status() {
  const rows = useFleetStatus()
  // `dataUpdatedAt` is in the dependencies as well as the rows, and both halves
  // are load-bearing (DEV-308). Reading it here is what makes React Query track
  // it, so a poll that brought nothing new still re-renders this view; listing
  // it is what makes the panel recompute when it does. Depending on `rows.data`
  // alone leaves a tab open since this morning reporting this morning's pass as
  // current, for ever, which is the one thing the banner exists to stop.
  const panel = useMemo(() => statusPanelFrom(rows), [rows.data, rows.dataUpdatedAt])

  if (rows.error) return <QueryError error={rows.error} />

  return (
    <>
      <ViewHeader title="Status" aside={rows.data ? panel.headline : 'Reading the fleet…'} />

      {/* The banner is a statement about this page, not about the fleet, so it
          sits above everything it qualifies: when the agent has stopped
          publishing, every count below it is a claim about whenever it last
          ran. Clay and a sentence, not a colour-coded alert. */}
      {panel.downSince ? (
        <Card style={{ marginBottom: 14 }}>
          <div className={css.statusDown}>
            {panel.downSince}
            <span className={css.statusDownNote}>
              Nothing below has been checked since then. The jobs may be fine; this page cannot
              currently tell you either way.
            </span>
          </div>
        </Card>
      ) : null}

      {!rows.data ? null
        : panel.generatedAt === null ? (
          <Card>
            <Empty glyph="[ ]">
              The status job has not published a pass yet. This page fills itself the first time it
              runs — there is nothing to set up here.
            </Empty>
          </Card>
        ) : (
          <>
            {/* Shown only when there is something in it. An empty "Needs
                attention" heading is a thing to read past every morning. */}
            {panel.attention.length > 0 ? (
              <Card style={{ marginBottom: 14 }}>
                <div className={css.statusHead}>
                  <MicroLabel>Needs attention</MicroLabel>
                </div>
                <div className={`${css.statusRowHead} ${css.statusRow}`}>
                  <MicroLabel>Job</MicroLabel>
                  <MicroLabel>Where</MicroLabel>
                  <MicroLabel>Status</MicroLabel>
                  <MicroLabel>What it says</MicroLabel>
                </div>
                {panel.attention.map((j) => <AttentionRow key={j.job} job={j} />)}
              </Card>
            ) : null}

            {panel.queue ? (
              <Card style={{ marginBottom: 14 }}>
                <div className={css.statusQueue}>
                  <MicroLabel>Approval queue</MicroLabel>
                  <div className={css.statusQueueLine}>{panel.queue.note}</div>
                  <Link to={panel.queue.href} className={css.statusQueueLink}>Open the inbox →</Link>
                </div>
              </Card>
            ) : null}

            <Card>
              <Disclosure label title="All clear" summary={clearLine(panel.clear)}>
                {panel.clear.length === 0 ? (
                  <p className={css.findingNone}>No job is reporting OK.</p>
                ) : (
                  <>
                    <div className={`${css.statusRowHead} ${css.statusClearRow}`}>
                      <MicroLabel>Job</MicroLabel>
                      <MicroLabel>Where</MicroLabel>
                      <MicroLabel>Last run</MicroLabel>
                      <MicroLabel>Next due</MicroLabel>
                    </div>
                    {panel.clear.map((j) => <ClearRow key={j.job} job={j} />)}
                  </>
                )}
              </Disclosure>
            </Card>
          </>
        )}

      <Notice>
        <span style={{ padding: '0 18px', display: 'block' }}>
          Read-only, and read from one table. Every line on this page is what the status agent
          wrote on its last pass — opening it checks nothing and starts nothing, and a job is
          fixed where it runs, not here.
        </span>
      </Notice>
    </>
  )
}

/** The job's name, linking out where the agent gave somewhere to go. */
function JobName({ job }: { job: StatusJob }) {
  return job.link
    ? <a href={job.link} target="_blank" rel="noreferrer">{job.job}</a>
    : <span>{job.job}</span>
}

/**
 * Last run and next due, as one muted line. `until` writes a due time that has
 * already passed as an age, so a late job reads as late without this row
 * deciding that it is -- which is the agent's call, not the panel's.
 */
function Schedule({ job }: { job: StatusJob }) {
  return (
    <>
      <span>{job.lastRunAt ? `last run ${age(job.lastRunAt)}` : 'never run'}</span>
      {job.lastResult && job.lastResult !== 'success' ? <span>{job.lastResult}</span> : null}
      <span>{job.nextDueAt ? `next ${until(job.nextDueAt)}` : 'not scheduled'}</span>
    </>
  )
}

function AttentionRow({ job }: { job: StatusJob }) {
  return (
    <div className={css.statusRow}>
      <div className={`${css.statusJob} mono`}><JobName job={job} /></div>
      <div className={css.statusWhere}>{job.where}</div>
      <div className={css.statusState} style={{ color: `var(--s-${job.token})` }}>{job.statusLabel}</div>
      <div>
        <div className={css.statusNote}>{job.note}</div>
        <div className={`${css.statusMeta} mono`}><Schedule job={job} /></div>
      </div>
    </div>
  )
}

/**
 * A healthy job is its last run and its next due and nothing else: it is on the
 * page so that "twenty-seven OK" can be checked, not so that it can be read.
 */
function ClearRow({ job }: { job: StatusJob }) {
  return (
    <div className={`${css.statusRow} ${css.statusClearRow}`}>
      <div className={`${css.statusJob} mono`}><JobName job={job} /></div>
      <div className={css.statusWhere}>{job.where}</div>
      <div className={`${css.statusMeta} mono`}>{job.lastRunAt ? age(job.lastRunAt) : 'never run'}</div>
      <div className={`${css.statusMeta} mono`}>{job.nextDueAt ? until(job.nextDueAt) : 'not scheduled'}</div>
    </div>
  )
}
